import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Input, Label } from 'reactstrap';
import { TypedMemo } from '../../../../../common/types';
import { ConfirmDialog } from '../../../../../common/views/modals/confirmDialog/ConfirmDialog';
import { useSMLRouter } from '../../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../../routing/sml/SMLRoutes';
import { platformName } from '../../../platformMeta';
import { SocialPlatform } from '../../../types';
import { ChannelMark } from './ChannelMark';
import { useRollout } from './RolloutContext';
import { rolloutMessages } from './rolloutMessages';
import { zoneCity } from './rolloutTime';
import { channelsByPostingMode } from './launch/postingModes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the run sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelsLineProps {
  /** The scheduled posts of this month, on a plan with a limit: "June: 46 of 200 scheduled posts used". */
  quota: { month: string; used: number; limit: number } | null;
}

/**
 * The channels line (design rollout-plan a1, b1 `.chline`): the channels of the plan, the time zone
 * of the times, Reconnect, the month count and "Change channels" (the default of new steps, owner
 * decision 6). With no channel it says "Connect a channel to plan posts". It names which channels
 * post by themselves and which give a "post it yourself" reminder (QA finding F02); with no
 * connected channel it says so and offers "Connect a channel".
 */
const ChannelsLineComponent: React.FC<ChannelsLineProps> = ({ quota }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const { channels, plan, timeZone, actions, canAct } = useRollout();
  const [isOpen, setIsOpen] = useState(false);
  const usable = channels.map((channel) => channel.platform);
  const current = plan.defaultPlatforms ?? usable;
  const [picked, setPicked] = useState<SocialPlatform[]>([]);
  const openChannels = useCallback(() => router.push(SMLRoutes.promoteChannels), [router]);
  const openDialog = useCallback(() => {
    setPicked(usable.filter((platform) => current.includes(platform)));
    setIsOpen(true);
  }, [current, usable]);
  const toggle = (platform: SocialPlatform) =>
    setPicked((list) => (list.includes(platform) ? list.filter((one) => one !== platform) : [...list, platform]));
  const save = () => {
    actions
      .setDefaultChannels('channels', picked)
      .then((ok) => (ok ? setIsOpen(false) : undefined))
      .catch(console.error);
  };

  if (channels.length === 0) {
    return (
      <div className={styles.chline}>
        <button type="button" className={styles.link} onClick={openChannels}>
          {intl.formatMessage(rolloutMessages.channelsNone)}
        </button>
      </div>
    );
  }
  const shown = channels.filter((channel) => current.includes(channel.platform));
  const modes = channelsByPostingMode(shown);
  const names = (list: typeof shown): string => list.map((channel) => platformName(channel.platform)).join(', ');
  const reconnect = channels
    .filter((channel) => channel.needsReconnect)
    .map((channel) => platformName(channel.platform));
  const city = zoneCity(timeZone);

  return (
    <div className={styles.chline}>
      <span className={styles.chns}>
        {shown.map((channel) => (
          <ChannelMark key={channel.platform} platform={channel.platform} small />
        ))}
      </span>
      <span>
        {modes.auto.length > 0 && (
          <span data-testid="channels-auto">
            <b>{intl.formatMessage(rolloutMessages.channelsAuto)}</b> {names(modes.auto)}.{' '}
          </span>
        )}
        {modes.manual.length > 0 && (
          <span data-testid="channels-manual">
            <b>{intl.formatMessage(rolloutMessages.channelsManual)}</b> {names(modes.manual)}.{' '}
          </span>
        )}
        {modes.auto.length === 0 && <>{intl.formatMessage(rolloutMessages.channelsNoneConnected)} </>}
        {intl.formatMessage(rolloutMessages.channelsLine)}{' '}
        {city !== null && intl.formatMessage(rolloutMessages.channelsZone, { zone: city })}{' '}
        {reconnect.length > 0 &&
          intl.formatMessage(rolloutMessages.channelsReconnect, {
            names: reconnect.join(', '),
            count: reconnect.length,
          })}
      </span>
      <span className={styles.grow} />
      {modes.auto.length === 0 && (
        <button type="button" className={styles.link} onClick={openChannels}>
          {intl.formatMessage(rolloutMessages.connectChannel)}
        </button>
      )}
      {quota !== null && (
        <span className={styles.cost}>{intl.formatMessage(rolloutMessages.channelsQuota, quota)}</span>
      )}
      <button type="button" className={styles.link} disabled={!canAct} onClick={openDialog}>
        {intl.formatMessage(rolloutMessages.changeChannels)}
      </button>
      <ConfirmDialog
        isOpen={isOpen}
        tone="neutral"
        title={intl.formatMessage(rolloutMessages.changeChannelsTitle)}
        description={intl.formatMessage(rolloutMessages.changeChannelsBody)}
        confirmLabel={intl.formatMessage(rolloutMessages.changeChannelsSave)}
        isBusy={actions.busy === 'channels'}
        error={actions.feedback.channels?.tone === 'danger' ? actions.feedback.channels.message : undefined}
        onConfirm={save}
        onCancel={() => setIsOpen(false)}
      >
        <div className="d-flex flex-column gap-2">
          {usable.map((platform) => (
            <Label key={platform} check className="d-flex align-items-center gap-2">
              <Input type="checkbox" checked={picked.includes(platform)} onChange={() => toggle(platform)} />
              <ChannelMark platform={platform} small />
              {platformName(platform)}
            </Label>
          ))}
        </div>
      </ConfirmDialog>
    </div>
  );
};

export const ChannelsLine = TypedMemo(ChannelsLineComponent);
